import { useState, Fragment } from "react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PL_ROWS, fmtCell, VarBadge, MONTHS_ES, num, pct } from "./faUtils";

/** Tabla Budget | Real | Varianza del mes seleccionado + YTD + Total Año.
 *  Iter117: acepta `month`/`onMonthChange` opcionales para permitir que el
 *  selector global de Análisis Financiero controle el mes desde afuera. */
export default function FaBudgetReal({ report, month: monthProp, onMonthChange }) {
  const withData = report.months.filter((m) => m.budget_has_data || m.real_has_data).map((m) => m.month);
  const [monthLocal, setMonthLocal] = useState(withData.length ? withData[withData.length - 1] : new Date().getMonth() + 1);
  const month = monthProp ?? monthLocal;
  const setMonth = onMonthChange ?? setMonthLocal;
  const mo = report.months[month - 1];
  const ytdB = report.ytd.budget || {};
  const ytdR = report.ytd.real || {};
  const totB = report.totals.budget || {};

  const varFor = (row) => {
    const b = Number(mo.budget?.[row.k] || 0);
    const r = Number(mo.real?.[row.k] || 0);
    const diff = Math.round((r - b) * 100) / 100;
    const p = b !== 0 ? Math.round(diff / Math.abs(b) * 1000) / 10 : 0;
    // En costos, subir es malo → invert
    const invert = row.neg || row.k.startsWith("costo") || row.k === "break_even_ml";
    if (row.t === "pct") {
      return <span className={`text-[11px] font-bold ${ (invert ? diff <= 0 : diff >= 0) ? "text-emerald-700" : "text-red-700"}`}>{diff > 0 ? "+" : ""}{num(diff)} pp</span>;
    }
    if (row.t === "num") {
      return <span className={`text-[11px] font-bold ${ (invert ? diff <= 0 : diff >= 0) ? "text-emerald-700" : "text-red-700"}`}>{diff > 0 ? "+" : ""}{num(diff)}</span>;
    }
    return <VarBadge diff={diff} pct={p} invert={invert} />;
  };

  return (
    <div className="space-y-4 pt-3">
      <div className="flex flex-wrap items-center gap-3">
        <Select value={String(month)} onValueChange={(v) => setMonth(Number(v))}>
          <SelectTrigger className="w-40" data-testid="fa-br-month-select"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-white">
            {MONTHS_ES.map((l, i) => (
              <SelectItem key={i} value={String(i + 1)}>{l} {report.year}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <p className="text-xs text-slate-500">
          Cumplimiento ML del mes: <span className="font-bold text-slate-900">{pct(mo.cumplimiento_pct)}</span>
          {mo.real?.esc_factor && mo.real.esc_factor !== 1 && (
            <> · Factor escalatoria: <span className="font-bold text-slate-900">{mo.real.esc_factor}</span></>
          )}
        </p>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg shadow-sm overflow-x-auto">
        <table className="w-full text-sm min-w-[820px]" data-testid="fa-br-table">
          <thead>
            <tr className="text-[10px] uppercase tracking-widest text-slate-400 border-b-2 border-slate-200 bg-slate-50">
              <th className="text-left py-2 px-3">Concepto</th>
              <th className="text-right px-3 text-blue-900">Budget {mo.label}</th>
              <th className="text-right px-3 text-slate-900">Real {mo.label}</th>
              <th className="text-right px-3">Varianza</th>
              <th className="text-right px-3 border-l border-slate-200">YTD Budget</th>
              <th className="text-right px-3">YTD Real</th>
              <th className="text-right px-3 border-l border-slate-200">Año Budget</th>
            </tr>
          </thead>
          <tbody>
            {PL_ROWS.map((row) => (
              <Fragment key={row.k}>
                {row.section && (
                  <tr className="bg-blue-950">
                    <td colSpan={7} className="py-1 px-3 text-[10px] uppercase tracking-[0.2em] font-bold text-blue-100">{row.section}</td>
                  </tr>
                )}
                <tr className={`border-b border-slate-100 ${row.highlight ? "bg-amber-50/70" : row.bold ? "bg-slate-50" : ""}`}>
                  <td className={`py-1.5 px-3 ${row.bold ? "font-bold text-slate-900" : "text-slate-600"}`}>{row.l}</td>
                  <td className="text-right px-3 text-blue-900/80 tabular-nums">{mo.budget_has_data ? fmtCell(row, mo.budget?.[row.k]) : "—"}</td>
                  <td className={`text-right px-3 tabular-nums ${row.bold ? "font-bold text-slate-900" : "text-slate-700"}`}>{mo.real_has_data ? fmtCell(row, mo.real?.[row.k]) : "—"}</td>
                  <td className="text-right px-3">{mo.budget_has_data && mo.real_has_data ? varFor(row) : <span className="text-slate-300 text-[11px]">—</span>}</td>
                  <td className="text-right px-3 tabular-nums text-blue-900/60 border-l border-slate-100">{fmtCell(row, ytdB[row.k])}</td>
                  <td className="text-right px-3 tabular-nums text-slate-600">{fmtCell(row, ytdR[row.k])}</td>
                  <td className="text-right px-3 tabular-nums text-slate-500 border-l border-slate-100">{fmtCell(row, totB[row.k])}</td>
                </tr>
              </Fragment>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-[11px] text-slate-400 italic">
        Depreciación, intereses y CAPEX se calculan automáticamente desde el catálogo de equipos (Parámetros).
        Mano de obra = sueldo base × (1 + factor IMSS) + destajos. Los YTD corren hasta el último mes con datos reales.
      </p>
    </div>
  );
}
